/* eslint-disable max-classes-per-file */

/*
The contents of this file are originally based on `prompt.ts` from the `prosemirror-example-setup` repo,
but have been heavily modified to suit our needs.
https://github.com/ProseMirror/prosemirror-example-setup/blob/master/src/prompt.ts
*/

import {
  type VirtualElement,
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
} from "@floating-ui/dom";
import { Attrs } from "prosemirror-model";
import type { Selection } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import { t } from "i18n/i18n";

/** Creates a VirtualElement for positioning the popover at the selection. */
export function createSelectionElement(view: EditorView, selection: Selection): VirtualElement {
  return {
    getBoundingClientRect() {
      const coords = view.coordsAtPos(selection.head);

      return {
        x: coords.left,
        y: coords.top,
        width: coords.right - coords.left,
        height: coords.bottom - coords.top,
        left: coords.left,
        right: coords.right,
        top: coords.top,
        bottom: coords.bottom,
      };
    },
  };
}

/** Opens a popover near the given selection element. */
export function openPopover(options: {
  appendTo?: HTMLElement;
  selectionEl: VirtualElement;
  render: (close: () => void) => HTMLElement;
  focusEl?: HTMLElement;
  onClose?: () => void;
}) {
  const appendTo = options.appendTo || document.body;
  const wrapper = appendTo.appendChild(document.createElement("div"));
  wrapper.className =
    "popover absolute z-[11] w-[390px] max-w-full min-w-0 rounded-[12px] border border-black-tint-90 bg-white p-6 shadow-modal supports-squircle:rounded-[24px] supports-squircle:corner-squircle";

  let autoUpdateCleanup: (() => void) | undefined;
  let isClosing = false;

  const mouseOutside = (e: MouseEvent) => {
    if (!wrapper.contains(e.target as HTMLElement)) close();
  };

  const mouseInside = (e: MouseEvent) => {
    const target = e.target;
    if (!(target instanceof HTMLElement) || target.closest("button, input, textarea, a")) return;
    e.preventDefault(); // Prevent toolbar changes due to focus events
  };

  wrapper.addEventListener("mousedown", mouseInside);
  setTimeout(() => window.addEventListener("mousedown", mouseOutside), 50);

  let close = () => {
    if (isClosing) return;
    isClosing = true;
    window.removeEventListener("mousedown", mouseOutside);
    wrapper.removeEventListener("mousedown", mouseInside);
    if (autoUpdateCleanup) autoUpdateCleanup();
    if (options.onClose) options.onClose();
    if (wrapper.parentNode) wrapper.parentNode.removeChild(wrapper);
  };

  wrapper.appendChild(options.render(close));

  let hasFocused = false;
  autoUpdateCleanup = autoUpdate(options.selectionEl, wrapper, () => {
    computePosition(options.selectionEl, wrapper, {
      placement: "bottom",
      middleware: [
        offset(3),
        flip({
          crossAxis: false,
          padding: {
            top: 40,
          },
        }),
        shift({
          boundary: appendTo,
          padding: {
            top: 3,
            bottom: 3,
            left: 3,
            right: 3,
          },
        }),
      ],
    }).then((position) => {
      wrapper.style.left = `${position.x}px`;
      wrapper.style.top = `${position.y}px`;
      if (options.focusEl && !hasFocused) {
        hasFocused = true;
        options.focusEl.focus();
      }
    });
  });
}

/** Opens a popover near the given selection element, with a form containing the given fields. */
export function openPopoverForm(options: {
  appendTo?: HTMLElement;
  selectionEl: VirtualElement;
  title: string;
  fields: { [name: string]: Field };
  callback: (attrs: Attrs) => void;
  onClose?: () => void;
}) {
  const domFields: HTMLElement[] = [];
  for (const field of Object.values(options.fields)) {
    domFields.push(field.render());
  }

  return openPopover({
    appendTo: options.appendTo,
    selectionEl: options.selectionEl,
    onClose: options.onClose,
    render: (close) => {
      const error = document.createElement("div");
      error.className = "popover-invalid hidden text-sm text-red";

      const createWrapper = () => {
        const form = document.createElement("form");
        form.className = "flex w-full flex-col gap-2";

        const submit = () => {
          form.dataset.submitted = "true";
          const params = getValues(options.fields, domFields);
          if (params) {
            close();
            options.callback(params);
          }
        };

        form.addEventListener("submit", (e) => {
          e.preventDefault();
          submit();
        });

        form.addEventListener("keydown", (e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            close();
          } else if (e.key === "Tab") {
            window.setTimeout(() => {
              if (!form.contains(document.activeElement)) close();
            }, 500);
          }
        });

        return form;
      };

      const addFields = (form: HTMLFormElement) => {
        const fields = document.createElement("div");
        fields.className = "flex w-full flex-col gap-2";

        form.appendChild(fields);

        if (options.title) {
          const title = document.createElement("h3");
          title.textContent = options.title;
          fields.appendChild(title);
        }
        domFields.forEach((field) => {
          const fieldWrapper = document.createElement("div");
          fieldWrapper.className = "w-full";
          fieldWrapper.appendChild(field);
          fields.appendChild(fieldWrapper);
        });
        form.appendChild(error);
      };

      const addButtons = (form: HTMLFormElement) => {
        const buttons = form.appendChild(document.createElement("div"));
        buttons.className = "mt-2 flex flex-row items-center justify-between";

        const cancelButton = document.createElement("button");
        cancelButton.type = "button";
        cancelButton.className = "popover-button popover-button-cancel";
        cancelButton.textContent = t("common.cancel");
        cancelButton.addEventListener("click", close);
        buttons.appendChild(cancelButton);

        const submitButton = document.createElement("button");
        submitButton.type = "submit";
        submitButton.className = "popover-button popover-button-submit";
        submitButton.textContent = t("common.save");
        submitButton.disabled = !hasNonEmptyField(domFields);
        buttons.appendChild(submitButton);

        const updateSubmitState = () => {
          submitButton.disabled = !hasNonEmptyField(domFields);
        };
        domFields.forEach((field) => field.addEventListener("input", updateSubmitState));
      };

      const form = createWrapper();
      addFields(form);
      addButtons(form);

      return form;
    },
    focusEl: domFields[0],
  });
}

function hasNonEmptyField(domFields: readonly HTMLElement[]): boolean {
  return domFields.some((field) => {
    const value = (field as HTMLInputElement | HTMLTextAreaElement).value;
    return value.trim() !== "";
  });
}

function getValues<F extends { [name: string]: Field }>(
  fields: F,
  domFields: readonly HTMLElement[],
): Record<keyof F, string | null> | null {
  const result = Object.create(null) as Record<keyof F, string | null>;
  let i = 0;
  for (const name in fields) {
    if (Object.hasOwn(fields, name)) {
      const field = fields[name];
      const dom = domFields[i];
      i += 1;
      const value = field.read(dom);
      const bad = field.validate(value);
      if (bad) {
        reportInvalid(dom, bad);
        return null;
      }
      result[name] = value;
    }
  }
  return result;
}

function reportInvalid(dom: HTMLElement, message: string) {
  const wrapper = dom.closest(".popover");
  if (!wrapper) return;
  const error = wrapper.querySelector(".popover-invalid");
  if (!error) return;
  error.textContent = message;
  error.classList.remove("hidden");
  // eslint-disable-next-line no-param-reassign
  dom.ariaInvalid = "true";
  // eslint-disable-next-line no-param-reassign
  dom.ariaErrorMessageElements = [error];
}

function clearInvalid(dom: HTMLElement) {
  const form = dom.closest("form") as HTMLElement | null;
  if (!form || form.dataset.submitted !== "true") {
    return;
  }
  const wrapper = form.closest(".popover");
  if (!wrapper) return;
  const error = wrapper.querySelector(".popover-invalid");
  if (!error) return;
  error.textContent = "";
  error.classList.add("hidden");
  // eslint-disable-next-line no-param-reassign
  dom.ariaInvalid = "false";
  // eslint-disable-next-line no-param-reassign
  dom.ariaErrorMessageElements = [];
}

interface FieldOptions {
  name: string;
  value?: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  transform?: (value: string | null) => string | null;
  validate?: (value: string | null) => string | null;
}

export abstract class Field<Options extends FieldOptions = FieldOptions> {
  readonly options: Options;

  constructor(options: Options) {
    this.options = options;
  }

  /** Render the field to the DOM. Should be implemented by all subclasses. */
  abstract render(): HTMLElement;

  /** Read the field's value from its DOM node. */
  read(dom: HTMLElement) {
    const value = (dom as HTMLInputElement | HTMLTextAreaElement).value;
    if (this.options.transform) {
      return this.options.transform(value);
    }
    return value;
  }

  /** A field-type-specific validation function. */
  // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-unused-vars
  validateType(value: string | null): string | null {
    return null;
  }

  /** Runs required-field check, then validateType, then the custom validate option. */
  validate(value: string | null): string | null {
    if (!value && this.options.required) return t("validation.required");
    return (
      this.validateType(value) || (this.options.validate ? this.options.validate(value) : null)
    );
  }
}

export class TextField extends Field {
  render() {
    const input = document.createElement("input");
    input.type = "text";
    input.className =
      "h-11 w-full min-w-0 rounded border border-black-tint-90 px-3 py-2 text-base text-black-tint-20 outline-none placeholder:text-black-tint-70";
    if (this.options.name) input.name = this.options.name;
    if (this.options.label) input.ariaLabel = this.options.label;
    if (this.options.placeholder) input.placeholder = this.options.placeholder;
    input.value = this.options.value || "";
    input.autocomplete = "off";
    input.onkeydown = () => clearInvalid(input);

    return input;
  }
}

export class TextareaField extends Field {
  render() {
    const textarea = document.createElement("textarea");

    textarea.className =
      "h-20 w-full min-w-0 resize-y overflow-x-auto overflow-y-auto rounded border border-black-tint-90 px-3 py-2 text-base text-black-tint-20 outline-none placeholder:text-black-tint-70";

    textarea.rows = 3;
    textarea.wrap = "off";
    if (this.options.name) textarea.name = this.options.name;
    if (this.options.label) textarea.ariaLabel = this.options.label;
    if (this.options.placeholder) textarea.placeholder = this.options.placeholder;
    textarea.value = this.options.value || "";
    textarea.autocomplete = "off";
    textarea.onkeydown = () => clearInvalid(textarea);

    return textarea;
  }
}
